import React, { useState, useRef, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '';

// Debounced tag input: suggests existing tags from GET /api/tags?q=... as the
// user types (fetch-as-you-type), while still allowing Enter/Add with no
// selection to commit free text as a new tag — the backend's resolveTag()
// auto-creates unknown tags, so this never blocks tagging with something new.
export default function TagAutocompleteInput({
  existingTags = [],
  onAdd,
  disabled = false,
  placeholder = 'Add tag...',
  tagType,
  inputClassName,
  addButtonClassName
}) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!query.trim()) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ q: query.trim() });
        if (tagType) params.set('type', tagType);
        const res = await fetch(`${API_URL}/api/tags?${params.toString()}`);
        if (!res.ok) return;
        const data = await res.json();
        setSuggestions((data.tags || []).filter(t => !existingTags.includes(t.name)));
        setOpen(true);
        setHighlighted(-1);
      } catch (e) {
        // Autocomplete is a convenience — a failed lookup shouldn't block typing/adding.
      }
    }, 200);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, tagType]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const commit = (value) => {
    const trimmed = (value || '').trim();
    if (!trimmed) return;
    onAdd(trimmed);
    setQuery('');
    setSuggestions([]);
    setOpen(false);
    setHighlighted(-1);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (highlighted >= 0 && suggestions[highlighted]) {
        commit(suggestions[highlighted].name);
      } else {
        commit(query);
      }
    } else if (e.key === 'ArrowDown') {
      if (suggestions.length > 0) {
        e.preventDefault();
        setHighlighted(h => Math.min(h + 1, suggestions.length - 1));
      }
    } else if (e.key === 'ArrowUp') {
      if (suggestions.length > 0) {
        e.preventDefault();
        setHighlighted(h => Math.max(h - 1, -1));
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setHighlighted(-1);
    }
  };

  return (
    <div ref={containerRef} className="relative flex-1">
      <div className="flex gap-2">
        <input
          type="text"
          placeholder={placeholder}
          value={query}
          disabled={disabled}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          className={inputClassName}
        />
        <button
          type="button"
          onClick={() => commit(query)}
          disabled={disabled}
          className={addButtonClassName}
        >
          Add
        </button>
      </div>

      {open && suggestions.length > 0 && (
        <div className="absolute z-10 mt-1 w-full bg-zinc-800 border border-zinc-600 rounded shadow-lg max-h-48 overflow-y-auto">
          {suggestions.map((s, idx) => (
            <button
              type="button"
              key={s.id}
              onMouseDown={(e) => { e.preventDefault(); commit(s.name); }}
              className={`w-full text-left px-3 py-2 text-sm flex justify-between items-center ${
                idx === highlighted ? 'bg-zinc-700' : 'hover:bg-zinc-700'
              }`}
            >
              <span>{s.name}</span>
              <span className="text-xs text-zinc-500">{s.tag_type}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
